import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Fade,
  LinearProgress
} from "@mui/material";
import {
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  WarningAmber as WarningIcon,
  Speed as SpeedIcon,
  AccessTime as TimeIcon
} from "@mui/icons-material";
import { useCollection } from "../hooks/useCollection";
import { Order } from "../interfaces/Order";

// Utility to safely parse dates
const toDate = (order: any): Date => {
  if (!order) return new Date();
  const dateValue = order.dateSuccess || order.date || order.createdAt || order.timestamp;
  if (!dateValue) return new Date();

  let dateObj: Date;
  if (typeof dateValue.toDate === "function") {
    dateObj = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    dateObj = dateValue;
  } else if (typeof dateValue === "number") {
    dateObj = new Date(dateValue);
  } else if (dateValue && typeof dateValue === "object" && (dateValue.seconds !== undefined || dateValue._seconds !== undefined)) {
    const s = dateValue.seconds !== undefined ? dateValue.seconds : dateValue._seconds;
    dateObj = new Date(s * 1000);
  } else {
    dateObj = new Date(dateValue);
  }
  return isNaN(dateObj.getTime()) ? new Date() : dateObj;
};

interface RoleStat {
  roleName: string;
  count: number;
  totalDurationMinutes: number;
  averageTime: number;
}

interface WorkerStats {
  userId: string;
  userName: string;
  totalStagesCompleted: number;
  totalDurationMinutes: number;
  delayedCount: number; // > 15 mins
  fastCount: number;    // < 10 mins
  averageTime: number;
  rolesMap: Record<string, RoleStat>;
}

export default function WorkerMetricsPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { data: orders = [] } = useCollection<Order>(restaurantId!, "orderssuccess");

  const [dateRange, setDateRange] = useState<"today" | "week" | "month">("today");

  // Thresholds
  const DELAYED_THRESHOLD = 15;
  const FAST_THRESHOLD = 10;

  // Filter orders by date range
  const filteredOrders = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let startDate = startOfToday;
    if (dateRange === "week") {
      startDate = new Date(startOfToday);
      startDate.setDate(startDate.getDate() - 7);
    } else if (dateRange === "month") {
      startDate = new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1);
    }

    return orders.filter(order => {
      const orderDate = toDate(order);
      return orderDate >= startDate && order.state === "completada";
    });
  }, [orders, dateRange]);

  // Aggregate metrics
  const workerStats = useMemo(() => {
    const statsMap: Record<string, WorkerStats> = {};

    filteredOrders.forEach(order => {
      if (order.stageMetrics) {
        Object.values(order.stageMetrics).forEach(metric => {
          if (!metric.userId || !metric.userName) return;

          if (!statsMap[metric.userId]) {
            statsMap[metric.userId] = {
              userId: metric.userId,
              userName: metric.userName,
              totalStagesCompleted: 0,
              totalDurationMinutes: 0,
              delayedCount: 0,
              fastCount: 0,
              averageTime: 0,
              rolesMap: {}
            };
          }

          const stats = statsMap[metric.userId];
          stats.totalStagesCompleted += 1;
          stats.totalDurationMinutes += metric.durationMinutes || 0;

          const roleName = metric.userRole || "General";
          if (!stats.rolesMap[roleName]) {
            stats.rolesMap[roleName] = {
              roleName,
              count: 0,
              totalDurationMinutes: 0,
              averageTime: 0
            };
          }
          const roleStat = stats.rolesMap[roleName];
          roleStat.count += 1;
          roleStat.totalDurationMinutes += metric.durationMinutes || 0;

          if ((metric.durationMinutes || 0) > DELAYED_THRESHOLD) {
            stats.delayedCount += 1;
          } else if ((metric.durationMinutes || 0) <= FAST_THRESHOLD) {
            stats.fastCount += 1;
          }
        });
      }
    });

    // Calculate averages and convert to array
    return Object.values(statsMap).map(stats => {
      stats.averageTime = stats.totalStagesCompleted > 0 
        ? Math.round(stats.totalDurationMinutes / stats.totalStagesCompleted) 
        : 0;
      Object.values(stats.rolesMap).forEach(r => {
        r.averageTime = r.count > 0 ? Math.round(r.totalDurationMinutes / r.count) : 0;
      });
      return stats;
    }).sort((a, b) => b.totalStagesCompleted - a.totalStagesCompleted); // Sort by volume descending

  }, [filteredOrders]);

  // Highlights
  const topPerformer = useMemo(() => {
    if (workerStats.length === 0) return null;
    const minVolume = Math.max(1, Math.max(...workerStats.map(w => w.totalStagesCompleted)) * 0.2);
    const eligible = workerStats.filter(w => w.totalStagesCompleted >= minVolume);
    if (eligible.length === 0) return null;
    
    return eligible.reduce((prev, current) => {
      return (prev.averageTime < current.averageTime) ? prev : current;
    });
  }, [workerStats]);

  const needsAttention = useMemo(() => {
    if (workerStats.length === 0) return null;
    const sortedByDelay = [...workerStats].sort((a, b) => b.delayedCount - a.delayedCount);
    if (sortedByDelay[0].delayedCount > 0) {
      return sortedByDelay[0];
    }
    return null;
  }, [workerStats]);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pb: 6, px: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={4} spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ p: 1.5, bgcolor: "primary.main", borderRadius: 2, display: "flex", color: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
            <PeopleIcon />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: "-0.02em", color: "text.primary" }}>
              Métricas de Personal
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Rendimiento y tiempos de preparación por usuario y por rol desempeñado
            </Typography>
          </Box>
        </Stack>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Período</InputLabel>
          <Select
            value={dateRange}
            label="Período"
            onChange={(e) => setDateRange(e.target.value as any)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            <MenuItem value="today">Hoy</MenuItem>
            <MenuItem value="week">Últimos 7 días</MenuItem>
            <MenuItem value="month">Este Mes</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Fade in timeout={800}>
        <Grid container spacing={3} mb={5}>
          {/* Top Performer Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 4, border: "1px solid #dcfce7", bgcolor: "#f0fdf4", boxShadow: "none", height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Avatar sx={{ bgcolor: "#22c55e", width: 56, height: 56 }}>
                    <TrophyIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="success.main" fontWeight={800} textTransform="uppercase" letterSpacing={1}>
                      Empleado Destacado (Bono)
                    </Typography>
                    {topPerformer ? (
                      <>
                        <Typography variant="h5" fontWeight={900} color="text.primary" mt={0.5}>
                          {topPerformer.userName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          Promedio más rápido: <strong>{topPerformer.averageTime} min</strong> por tarea.
                          <br/>
                          Completó {topPerformer.totalStagesCompleted} tareas en este período.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        No hay suficientes datos en este período.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Needs Attention Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 4, border: "1px solid #fee2e2", bgcolor: "#fef2f2", boxShadow: "none", height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Avatar sx={{ bgcolor: "#ef4444", width: 56, height: 56 }}>
                    <WarningIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="error.main" fontWeight={800} textTransform="uppercase" letterSpacing={1}>
                      Requiere Atención
                    </Typography>
                    {needsAttention ? (
                      <>
                        <Typography variant="h5" fontWeight={900} color="text.primary" mt={0.5}>
                          {needsAttention.userName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          Tiene <strong>{needsAttention.delayedCount}</strong> tareas con retraso ({">"} {DELAYED_THRESHOLD} min).
                          <br/>
                          Promedio actual: {needsAttention.averageTime} min.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        ¡Excelente! Nadie tiene tareas demoradas.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      {/* Main Table */}
      <Typography variant="h6" fontWeight={800} mb={2}>
        Rendimiento General y por Rol
      </Typography>
      
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Empleado</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Desglose por Roles Desempeñados</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>Tareas Completadas</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>Tiempo Promedio Total</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>Rápidos ({"<"}{FAST_THRESHOLD}m)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>Demorados ({">"}{DELAYED_THRESHOLD}m)</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Eficiencia</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workerStats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No hay datos para el período seleccionado.
                </TableCell>
              </TableRow>
            ) : (
              workerStats.map(stat => {
                const total = Math.max(1, stat.totalStagesCompleted);
                const delayedPercent = (stat.delayedCount / total) * 100;
                
                return (
                  <TableRow key={stat.userId} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{stat.userName}</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Stack direction="row" flexWrap="wrap" gap={0.8}>
                        {Object.values(stat.rolesMap).map((roleItem) => (
                          <Chip
                            key={roleItem.roleName}
                            label={`${roleItem.roleName}: ${roleItem.count} tareas (${roleItem.averageTime}m avg)`}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 700, fontSize: "0.72rem" }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={stat.totalStagesCompleted} size="small" sx={{ fontWeight: 800, bgcolor: "#f1f5f9" }} />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                        <TimeIcon fontSize="small" sx={{ color: stat.averageTime > DELAYED_THRESHOLD ? "error.main" : "text.secondary" }} />
                        <Typography variant="body2" fontWeight={700} color={stat.averageTime > DELAYED_THRESHOLD ? "error.main" : "text.primary"}>
                          {stat.averageTime} min
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {stat.fastCount}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={700} color={stat.delayedCount > 0 ? "error.main" : "text.secondary"}>
                        {stat.delayedCount}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>Eficiencia</Typography>
                          <Typography variant="caption" color={delayedPercent > 20 ? "error.main" : "success.main"} fontWeight={700}>
                            {Math.round(100 - delayedPercent)}%
                          </Typography>
                        </Stack>
                        <LinearProgress 
                          variant="determinate" 
                          value={100 - delayedPercent} 
                          color={delayedPercent > 20 ? "error" : "success"}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
}
